/**
 * Which language a person reads this application in — the table, and the order that decides.
 *
 * THE MIRROR OF THIS FILE IS src/shared/i18n/languages.py, and a pytest reads BOTH and fails in
 * either direction (identity/backend/tests/test_language_preference.TestTheLanguageTableIsOnePlace).
 * Change one, change the other, or that test says so by name. Two copies exist because only the
 * browser knows the device language and the remembered choice, and only the server can validate a
 * write — but two copies with nothing watching them is how a screen and the record it files start
 * disagreeing about what a language is.
 *
 * WHERE THIS CAME FROM. Everything below already existed in modules/forms/i18n.js, serving one
 * screen. It was never forms-specific — only its path said otherwise — so it was PROMOTED here
 * verbatim and generalized on exactly two axes: `chrome(lang)` became `makeT(dict, lang)` in ./t.js
 * so any namespace can have a dictionary, and `pickLanguage` gained a `userPreference` rung that
 * wins outright. modules/forms/i18n.js now re-exports these names, so the QR page's import block did
 * not change one character and its 23 tests still pass unchanged.
 *
 * NO i18n LIBRARY, and the reasons are reasons rather than taste:
 *   1. THE RULE INVERTS. i18next's default on a missing key returns THE KEY. That is the one output
 *      forms/i18n.js forbids, and on a safety checklist it is worse than a blank. It can be
 *      configured away with three settings that must all stay right forever; `v || english` cannot
 *      be misconfigured.
 *   2. THE BACKEND MIRROR HAS NO EQUIVALENT. forms/backend/app/services/i18n.py renders the archived
 *      PDF and is explicitly this file's pair. There is no i18next for Python, so adopting one here
 *      guarantees the two grow different fallback rules — which is precisely the failure both
 *      headers already name.
 *   3. PROVIDER/SUSPENSE ON THE PUBLIC PAGE. PublicInspection.jsx deliberately avoids the shared api
 *      client; a Suspense-gated translation load is a blank screen for somebody on one bar of signal
 *      beside a machine.
 *   4. eslint sits AT its 80-error budget. A new dependency's plugin rules and a provider refactor
 *      are the wrong place to spend zero headroom.
 */

import { USER_STORAGE_KEY } from '../../config/config.js'

/** The language everything falls back to. A missing translation shows THIS. */
export const BASE_LANGUAGE = 'en'

// Each language's name IN ITS OWN LANGUAGE. Never a code (`ES` is not a word to someone who does not
// already know what it stands for) and never a flag — a flag is a country, and a Mexican hand and a
// Salvadoran hand share no flag but share this language.
export const LANGUAGE_NAMES = { en: 'English', es: 'Español' }

/** 'es-MX' -> 'es'. The subtag is a region, not a different language. */
export function primary(tag) {
  return String(tag || '').split('-')[0]
}

export function languageName(tag) {
  return LANGUAGE_NAMES[tag] || LANGUAGE_NAMES[primary(tag)] || String(tag || '')
}

/**
 * The offered language `tag` asks for, or null.
 *
 * Tolerant on both sides of the hyphen: a phone reporting 'es-MX' matches an app that speaks 'es',
 * and an app offering 'es-MX' matches a preference of 'es'. Neither side has to know the other's
 * regional habits, which is what stops a Salvadoran handset landing on English.
 */
export function matchLanguage(tag, offered) {
  if (!tag) return null
  const pool = (offered || []).filter(Boolean)
  if (pool.includes(tag)) return tag
  const root = primary(tag)
  return pool.find((o) => o === root || primary(o) === root) || null
}

/**
 * Which entry of `offered` a picker should show as SELECTED for a stored tag — '' for "no choice".
 *
 * ⚠ EVERY LANGUAGE CONTROL USED TO COMPARE WITH `===` AGAINST THE TABLE'S KEYS, and the server
 * deliberately accepts more tags than the table has keys: it shape-checks (`is_language_tag`) rather
 * than membership-checking, and both its error messages advertise `es-MX` by name. `matchLanguage`
 * then resolves `es-MX` to `es` and the whole application renders in Spanish — while the preference
 * card showed no radio selected at all, and the company-default dropdown fell back to its first
 * option and reported "No company language" for a default that was live and in force. A control that
 * says nothing is set, on a screen that is in Spanish because something is, is worse than a wrong
 * label: the next touch of it silently replaces a setting the admin could not see.
 *
 * A tag that matches nothing offered comes back UNCHANGED rather than as ''. The caller can then
 * show it verbatim instead of claiming no choice was ever made — see CompanyLanguageSetting.
 */
export function selectedLanguage(tag, offered) {
  if (!tag) return ''
  return matchLanguage(tag, offered || Object.keys(LANGUAGE_NAMES)) || tag
}

// ── remembering the choice ────────────────────────────────────────────────────────────────────────

/** The device choice made INSIDE the application (the login page's toggle, the header, Profile). */
export const LANGUAGE_STORAGE_KEY = 'tynvara.lang'

/**
 * The device choice made on the QR sticker page, which is a different population making a different
 * decision: an anonymous hand choosing among the languages THAT SHEET is written in, before any
 * account exists. Kept as its own key rather than merged, because the two offered sets are not the
 * same set — a sheet may be translated into a language the app is not, and vice versa.
 *
 * The app reads it as a SECONDARY signal (see `readStoredLanguage`) so the sign-in page a hand
 * reaches straight after a sticker is already in the language they just chose. One-directional on
 * purpose: the QR page never reads the app's key, so nothing here can move a sheet a hand is
 * standing over.
 */
export const PUBLIC_LANGUAGE_STORAGE_KEY = 'tynvara.publicInspection.lang'

function readKey(key) {
  try {
    return window.localStorage.getItem(key) || ''
  } catch (err) {
    // Safari private mode throws on localStorage. A language preference is not worth a crash.
    return ''
  }
}

/**
 * The choice remembered in this browser, or '' for none.
 *
 * ⚠ AN EMPTY APP KEY IS A DECISION, NOT AN ABSENCE, and the difference matters. Choosing "use my
 * device's language" WRITES an empty string here — so a present-but-empty key means "I deliberately
 * un-pinned this", and the QR page's key must NOT then be consulted. Only a key that was never
 * written at all falls through to it. Without that distinction, somebody who picked Español at a
 * machine and later asked the application to follow their phone would keep getting Spanish and have
 * no way to find out why.
 *
 * ⚠ AND THE STICKER'S KEY IS A PRE-AUTH SIGNAL ONLY. It is written by an ANONYMOUS hand at a sheet —
 * field hands have no logins at all; they identify with a badge and a PIN — so it says something
 * about the person holding the phone at that moment and nothing about whoever signs in next on the
 * shop phone. It reaches the sign-in page, which is the screen it was always really for: a hand who
 * chose Español at a machine finds the next page already in Spanish. It stops there. Once a session
 * exists, the person has rungs of their own — their account setting, and the sign-in page's toggle,
 * whose choice this browser keeps through a sign-out (shared/auth/signOut.js).
 */
export function readStoredLanguage() {
  let own = null
  try {
    own = window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
  } catch (err) { /* see readKey */ }
  if (typeof own === 'string') return own
  if (readKey(USER_STORAGE_KEY)) return ''
  return readKey(PUBLIC_LANGUAGE_STORAGE_KEY)
}

/** Remember a choice. `''` records "no choice" — see readStoredLanguage; it is not a removal. */
export function rememberLanguage(tag) {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, tag || '')
  } catch (err) { /* see readKey */ }
}

/** What this browser reports it reads. Array-valued because a phone can list several in order. */
export function deviceLanguages() {
  if (typeof navigator === 'undefined') return []
  const many = Array.isArray(navigator.languages) ? navigator.languages : []
  return (many.length ? many : [navigator.language]).filter(Boolean)
}

// ── the chain ─────────────────────────────────────────────────────────────────────────────────────

/**
 * The one function that decides, highest priority first. Mirrors resolve_language in
 * src/shared/i18n/languages.py.
 *
 *   (a) userPreference — the account's own setting. NULL until somebody chooses; a choice wins
 *       outright, because a person who has said what they read must never be argued with by a device.
 *   (b) storedChoice   — the visible toggle on the login page, remembered in this browser. This is
 *       what breaks the chicken-and-egg: it is reachable by somebody who cannot read the page it sits
 *       on, before any account exists to carry a preference.
 *   (c) device         — navigator.languages. A GUESS, so it may move a reader OFF the base language
 *       and NEVER back onto it. See below.
 *   (d) tenantDefault  — "our crews read Spanish", set once by an admin and host-resolved pre-auth so
 *       it reaches the login page too. Wrong for a mixed office, which is why (a) and (b) outrank it.
 *   (e) the base language.
 *
 * ⚠ THE ASYMMETRY AT (c) IS LOAD-BEARING and the plain reading of "device, then tenant default" hides
 * it. Almost every handset on a US job site reports `en-US` whatever its owner reads, so a flat
 * device-wins rule would make (d) INERT FOR EXACTLY THE TENANT WHO SET IT. So the device may upgrade
 * away from the base language and may never downgrade back onto it. This is the shipped behaviour of
 * the QR page carried forward unchanged, and both halves are tested — the Spanish phone that wins,
 * and the English phone that loses.
 *
 * `storedChoice` defaults to what this browser remembers. Pass it explicitly (even as '') to override.
 *
 * `base` is the language THIS pool falls back to, and it is an argument because a form version
 * carries its own `base_language`: a sheet authored in Spanish falls back to Spanish, and the
 * asymmetry at (c) has to be measured against that sheet's base rather than against the
 * application's. Defaults to English when the pool offers it, else the first language offered.
 */
export function resolveLanguage({
  userPreference, storedChoice, deviceLanguages: devices, tenantDefault, offered, base: baseIn,
} = {}) {
  const pool = (offered || Object.keys(LANGUAGE_NAMES)).filter(Boolean)
  if (!pool.length) return BASE_LANGUAGE
  const base = matchLanguage(baseIn, pool) || matchLanguage(BASE_LANGUAGE, pool) || pool[0]

  const stored = storedChoice !== undefined ? storedChoice : readStoredLanguage()
  const chosen = matchLanguage(userPreference, pool) || matchLanguage(stored, pool)
  if (chosen) return chosen

  const seen = (devices !== undefined ? devices : deviceLanguages()) || []
  const upgrade = seen.map((t) => matchLanguage(t, pool)).find((m) => m && m !== base)
  if (upgrade) return upgrade

  return matchLanguage(tenantDefault, pool) || base
}
